import React, {useCallback} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import Animated, {
  FadeInDown,
  FadeOut,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated';
import {Theme} from '../../theme/lightTheme';
import TextGeneric from '../../components/textGeneric';
import Icon from '../../components/icon';
import {LanguageListItemProps} from '../../interfaces/components/languageListItem';
import MyPressable from '../myPressable';
import LinearGradient from 'react-native-linear-gradient';
import {PixelRatio, Platform} from 'react-native';
import {isWeb} from '../../utils/responsive';

/**
 * Converts a country code to the corresponding flag emoji.
 *
 * @param {string} code - The country code to convert. Expected format is either 'flag-XX' or 'XX', where XX is the ISO 3166-1 alpha-2 code.
 * @returns {string} The corresponding flag emoji or an empty string if the code is invalid.
 */
const getFlagEmoji = (code: string): string => {
  try {
    const regionCode = code.replace('flag-', '').toUpperCase();
    if (regionCode.length !== 2) {
      throw new Error('Invalid country code length');
    }
    const codePoints = regionCode
      .split('')
      .map(char => 0x1f1e6 + char.charCodeAt(0) - 'A'.charCodeAt(0));

    return String.fromCodePoint(...codePoints);
  } catch (error: any) {
    return '';
  }
};

export const languageListItemHeight = 168 / PixelRatio.get();
const ContentContainer = styled(MyPressable)`
  width: 100%;
  height: 100%;
  padding: 4% 5%;
  justify-content: center;
`;
const LanguageItemContentContainer = styled(Animated.View)`
  width: 100%;
`;
const TextContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;
const Row = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;
const Text = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme}) => theme.text.title};
  padding-left: 3%;
  height: 100%;
`;
const Flag = styled.Text`
  font-size: ${({theme}) => theme.text.s4}px;
  line-height: ${({theme}) => theme.text.s4}px;
  width: ${({theme}) => theme.text.s1}px;
  aspect-ratio: 2;
`;

/**
 * Displays a Language List Item.
 * @param {boolean} isSelected Indicates whether the item is selected.
 * @param {LanguageListItemProps} props The properties of the LanguageListItem component.
 * @returns {React.ReactElement} The LanguageListItem component.
 */
const LanguageListItem: React.FC<LanguageListItemProps> = ({
  onPress,
  currentValue,
  item,
  style,
}): React.ReactElement => {
  const theme = useTheme() as Theme;

  const onPressHandler = useCallback(() => {
    onPress && onPress(item);
  }, [onPress]);

  return (
    <LinearGradient
      style={[
        {
          width: '90%',
          height: languageListItemHeight,
          alignSelf: 'center',
          borderRadius: 8,
          overflow: 'hidden',
        },
        style,
      ]}
      start={{x: 0.0, y: 0.25}}
      end={{x: 0.5, y: 1.0}}
      colors={[theme.text.light + '77', theme.border + '55']}>
      <LanguageItemContentContainer entering={FadeInDown} exiting={FadeOut}>
        <ContentContainer
          borderRadius={8}
          containerStyle={{
            alignSelf: 'center',
            height: languageListItemHeight,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderWidth: 1,
            borderColor: theme.border + '77',
            width: '100%',
          }}
          onPress={onPressHandler}>
          <Row>
            <TextContainer>
              {item.countryCode && (
                <Flag allowFontScaling={false}>
                  {getFlagEmoji(item.countryCode)}
                </Flag>
              )}
              <Text semiBold>{item.name}</Text>
            </TextContainer>
            {currentValue === item.code && (
              <Animated.View entering={ZoomIn} exiting={ZoomOut}>
                <Icon
                  name={'check'}
                  color={theme.primary}
                  size={theme.text.s6}
                />
              </Animated.View>
            )}
          </Row>
        </ContentContainer>
      </LanguageItemContentContainer>
    </LinearGradient>
  );
};

// Wrap with React.memo to add memoization
export default React.memo(LanguageListItem, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.currentValue === nextProps.currentValue &&
    prevProps.onPress === nextProps.onPress &&
    prevProps.style === nextProps.style
  );
});
